import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type DetailSidebarContextType = {
  isDetailSidebarOpen: boolean;
  detailSidebarWidth: number;
  detailSidebarTransition: boolean;
  setDetailSidebarOpen: (isOpen: boolean) => void;
  setDetailSidebarWidth: (width: number) => void;
  setDetailSidebarTransition: (enabled: boolean) => void;
};

type DetailSidebarProviderProps = {
  children: ReactNode;
};

const DEFAULT_WIDTH = 400;

const DetailSidebarContext = createContext<DetailSidebarContextType | null>(
  null,
);

export const useDetailSidebarContext = () => {
  const context = useContext(DetailSidebarContext);
  if (!context) {
    throw new Error(
      'useDetailSidebarContext must be used within a DetailSidebarProvider',
    );
  }
  return context;
};

export const DetailSidebarProvider = ({
  children,
}: DetailSidebarProviderProps) => {
  const [isDetailSidebarOpen, setIsDetailSidebarOpen] = useState(false);
  const [detailSidebarWidth, setWidth] = useState(DEFAULT_WIDTH);
  const [detailSidebarTransition, setTransition] = useState(true);

  const setDetailSidebarOpen = useCallback((isOpen: boolean) => {
    setIsDetailSidebarOpen(isOpen);
    if (!isOpen) {
      setWidth(DEFAULT_WIDTH);
    }
  }, []);

  const setDetailSidebarWidth = useCallback((width: number) => {
    setWidth(width);
  }, []);

  const setDetailSidebarTransition = useCallback((enabled: boolean) => {
    setTransition(enabled);
  }, []);

  const value = useMemo(
    () => ({
      isDetailSidebarOpen,
      detailSidebarWidth,
      detailSidebarTransition,
      setDetailSidebarOpen,
      setDetailSidebarWidth,
      setDetailSidebarTransition,
    }),
    [
      isDetailSidebarOpen,
      detailSidebarWidth,
      detailSidebarTransition,
      setDetailSidebarOpen,
      setDetailSidebarWidth,
      setDetailSidebarTransition,
    ],
  );

  return (
    <DetailSidebarContext.Provider value={value}>
      {children}
    </DetailSidebarContext.Provider>
  );
};
